// @flow

import React from 'react';
import _ from 'lodash';
import bemify, { toModifiers } from 'bemify-js';
import { compose, defaultProps, pure, withHandlers } from 'recompose';

import { createKeyHandler } from '@zapier/common-utils';

import Icon from 'app/common/components/Icon';
import { Div } from 'app/common/components/AccessibleElement';
import 'app/common/components/ToggleSwitch.scss';

import type { HOC } from 'recompose';

import type { KindType } from 'app/common/types';

const bem = bemify('toggle-switch');

const getAriaLabel = ({ isToggling, isOn, noun }) => {
  const onOrOff = isOn ? 'on' : 'off';
  return isToggling ? `turning ${onOrOff}` : `${noun} is ${onOrOff}`;
};

const ToggleSwitch = ({
  isOn,
  isDisabled,
  isToggling,
  kind,
  icon,
  onClick,
  className,
  noun,
  title,
}) => {
  return (
    <Div
      aria-disabled={isDisabled}
      aria-label={getAriaLabel({ isOn, isToggling, noun })}
      checked={isOn}
      className={bem(className, toModifiers(kind, { isDisabled }))}
      onClick={onClick}
      onKeyDown={createKeyHandler(['enter'], onClick)}
      role="checkbox"
      tabIndex={0}
      title={title}
    >
      <input
        aria-checked={isOn}
        aria-disabled={isDisabled}
        aria-label="onoffswitch"
        checked={!!isOn}
        className={bem('__toggle-checkbox', toModifiers(kind))}
        name="onoffswitch"
        onChange={_.noop}
        // required if 'checked' is initially on.
        type="checkbox"
        value={isOn ? 'on' : 'off'}
      />{' '}
      <span
        className={bem('__text-and-background', toModifiers(kind, { isOn }))}
        key="text-and-background"
      />
      {icon ? (
        <Icon
          className={bem(kind, '__icon', toModifiers({ isOn, isToggling }))}
          type="eye"
        />
      ) : (
        <span
          className={bem(
            '__toggle-dot',
            toModifiers(kind, { isOn, isToggling })
          )}
          key="toggle-dot"
        />
      )}
    </Div>
  );
};

type ToggleFuncType = (
  isOn: boolean,
  event: SyntheticEvent<HTMLElement>
) => void;

type ToggleSwitchPropTypes = {
  icon?: boolean,
  isDisabled?: boolean,
  isOn: boolean,
  isToggling?: boolean,
  kind?: KindType,
  // onClick is created by withHandlers for purity-- we need to be able
  // to pass the props isOn and onToggle, but don't want to
  // create a new func every time.
  // Creating the handler in withHandlers and using pure avoids a bunch of rerenders.
  onToggle: ToggleFuncType,
  onClick?: (
    isOn: boolean,
    onToggle: ToggleFuncType
  ) => (event: SyntheticEvent<HTMLElement>) => void,
  className?: string,
};

const enhanceToggleSwitch: HOC<*, ToggleSwitchPropTypes> = compose(
  pure,
  withHandlers({
    onClick: ({ isOn, onToggle }) => event => {
      event.preventDefault();
      // $FlowFixMe
      onToggle(!isOn, event);
    },
  }),
  defaultProps({
    icon: false,
    isDisabled: false,
    isOn: false,
    kind: '',
    className: '',
    noun: 'toggle',
    title: 'toggle',
  })
);

export default enhanceToggleSwitch(ToggleSwitch);
